import Panzoom from '@panzoom/panzoom'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Button, Frame, Input } from 'regen-ui'
import { tempo } from 'viem/chains'
import ArrowRight from '~icons/lucide/arrow-right'
import Fingerprint from '~icons/lucide/fingerprint'
import Move from '~icons/lucide/move'
import Square from '~icons/lucide/square'

import {
  AccessKeyPermissionsEditor,
  AccessKeyScopes,
} from '#/routes/remote/-components/AccessKeyScopes.js'
import * as AuthorizeFrames from '#/routes/remote/-frames/authorize/index.js'
import * as ConnectFrames from '#/routes/remote/-frames/connect/index.js'
import * as RequestFrames from '#/routes/remote/-frames/request/index.js'
import * as SignFrames from '#/routes/remote/-frames/sign/index.js'
import * as TransactionFrames from '#/routes/remote/-frames/transaction/index.js'
import * as ZoneFrames from '#/routes/remote/-frames/zones/index.js'
import { useDeposit } from '#/routes/remote/-hooks/useDeposit.js'
import * as ShowDeposit from '#/routes/remote/-lib/ShowDeposit.js'
import { AccentToggle, RadiusToggle, ThemeToggle } from '#/ui/ThemeToggle.js'

export const Route = createFileRoute('/design_/frames')({
  component: Wireframes,
})

const startScale = 0.75

function Wireframes() {
  const viewportRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const panzoomRef = useRef<ReturnType<typeof Panzoom>>(null)
  const [scrollLocked, setScrollLocked] = useState(false)

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    window.scrollTo(0, 0)
    viewport.scrollTo(0, 0)
  }, [scrollLocked])

  useEffect(() => {
    const previous = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    window.scrollTo(0, 0)
    return () => {
      window.history.scrollRestoration = previous
    }
  }, [])

  useEffect(() => {
    if (scrollLocked) return
    const canvas = canvasRef.current
    const viewport = viewportRef.current
    if (!canvas || !viewport) return

    const panzoom = Panzoom(canvas, {
      canvas: true,
      maxScale: 2,
      minScale: 0.25,
      startScale,
      startX: 0,
      startY: 0,
      step: 0.1,
    })
    panzoomRef.current = panzoom
    const id = window.requestAnimationFrame(() => {
      viewport.scrollTo(0, 0)
      panzoom.reset({ animate: false, startScale, startX: 0, startY: 0 })
    })

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      panzoom.zoomWithWheel(e)
    }
    viewport.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      window.cancelAnimationFrame(id)
      viewport.removeEventListener('wheel', onWheel)
      panzoom.destroy()
      panzoomRef.current = null
    }
  }, [scrollLocked])

  return (
    <div className="fixed inset-0 bg-background">
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <RadiusToggle />
        <AccentToggle />
        <ThemeToggle />
        <button
          type="button"
          aria-label={scrollLocked ? 'Enable pan & zoom' : 'Show at actual size'}
          aria-pressed={scrollLocked}
          className="flex items-center gap-2 cursor-pointer rounded-button border border-border bg-surface px-2.5 py-1.5 text-label-13 transition-colors hover:border-border-hover"
          onClick={() => setScrollLocked((v) => !v)}
        >
          {scrollLocked ? <Square className="size-3.5" /> : <Move className="size-3.5" />}
          {scrollLocked ? '1:1' : 'Zoom'}
        </button>
      </div>

      <div
        ref={viewportRef}
        className={
          scrollLocked
            ? 'absolute inset-0'
            : 'absolute inset-0 cursor-grab overflow-hidden active:cursor-grabbing'
        }
        style={{ overflow: scrollLocked ? 'auto' : undefined }}
      >
        <div
          ref={canvasRef}
          className="relative h-[4500px] w-[6000px]"
          style={{
            transformOrigin: '0 0',
            transform: scrollLocked ? 'none' : undefined,
            touchAction: scrollLocked ? 'auto' : undefined,
          }}
        >
          <FlowRow left={60} method="wallet_connect" title="Connect — New User" top={60}>
            <ConnectNewUserFlow />
          </FlowRow>

          <FlowRow
            left={2400}
            method="wallet_connect + showDeposit"
            title="Connect — New User + Deposit"
            top={60}
          >
            <ConnectNewUserDepositFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="wallet_connect + authorizeAccessKey"
            title="Connect — New User + Authorize"
            top={660}
          >
            <ConnectNewUserAuthorizeLazyFlow />
          </FlowRow>

          <FlowRow
            left={2400}
            method="wallet_connect + authorizeAccessKey + showDeposit"
            title="Connect — New User + Authorize + Deposit"
            top={1040}
          >
            <ConnectNewUserAuthorizeDepositFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="wallet_connect"
            title="Connect — Returning User (remembered)"
            top={1260}
          >
            <ConnectReturningPasskeyFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="wallet_connect + authorizeAccessKey"
            title="Connect — Returning User (remembered) + Authorize"
            top={1700}
          >
            <ConnectReturningPasskeyAuthorizeFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="wallet_connect"
            title="Connect — Returning User (fresh)"
            top={2300}
          >
            <ConnectReturningEmailFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="wallet_connect + authorizeAccessKey"
            title="Connect — Returning User (fresh) + Authorize"
            top={2900}
          >
            <ConnectReturningEmailAuthorizeFlow />
          </FlowRow>

          <FlowRow
            left={2400}
            method="wallet_connect + identity.email"
            title="Connect — New User + Verify Email (email)"
            top={1700}
          >
            <ConnectNewUserIdentityEmailFlow />
          </FlowRow>

          <FlowRow
            left={2400}
            method="wallet_connect + identity.email"
            title="Connect — New User + Verify Email (label)"
            top={2300}
          >
            <ConnectNewUserIdentityLabelFlow />
          </FlowRow>

          <FlowRow
            left={2400}
            method="wallet_connect + identity.email"
            title="Connect — Returning User + Verify Email"
            top={2900}
          >
            <ConnectReturningIdentityVerifyFlow />
          </FlowRow>

          <FlowRow left={2400} method="wallet_connect" title="Connect — Choose Account" top={3500}>
            <ConnectSelectAccountFlow />
          </FlowRow>

          <FlowRow left={60} method="eth_sendTransaction" title="Send Transaction" top={3500}>
            <SendTransactionFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="eth_sendTransaction"
            title="Send Transaction — Insufficient Balance"
            top={4100}
          >
            <SendTransactionOnrampFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="eth_sendTransaction"
            title="Send Transaction — Extras"
            top={4850}
          >
            <SendTransactionExtrasFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="eth_sendTransaction (transfer calls of same token)"
            title="Direct Payment (Preimage)"
            top={5450}
          >
            <DirectPaymentFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="eth_sendTransaction (transfer calls of same token)"
            title="Direct Payment (Preimage) — Extras"
            top={6050}
          >
            <DirectPaymentExtrasFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="eth_sendTransaction (approve call)"
            title="Token Approval (Preimage)"
            top={6650}
          >
            <TokenApprovalFlow />
          </FlowRow>

          <FlowRow
            left={1900}
            method="eth_sendTransaction (ERC-4626 vault call)"
            title="Vault Transaction (Preimage)"
            top={6650}
          >
            <VaultTransactionFlow />
          </FlowRow>

          <FlowRow left={60} method="wallet_deposit" title="Deposit" top={7250}>
            <DepositFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method={'wallet_deposit + to + displayName: "DoorDash"'}
            title="Deposit Recipient"
            top={7850}
          >
            <DepositRecipientFlow />
          </FlowRow>

          <FlowRow left={60} method="device-code" title="Request Results" top={8450}>
            <RequestResultFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="wallet_connect (via accounts/react-native)"
            title="Authorize Mobile"
            top={9050}
          >
            <AuthorizeMobileFlow />
          </FlowRow>

          <FlowRow
            left={1300}
            method="wallet_connect register + showDeposit (via accounts/react-native)"
            title="Authorize Mobile — Register + Deposit"
            top={9550}
          >
            <AuthorizeMobileRegisterDepositFlow />
          </FlowRow>

          <FlowRow left={60} method="personal_sign" title="Sign Message" top={9650}>
            <PersonalSignFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="personal_sign (raw hex)"
            title="Sign Message — Raw Data"
            top={10250}
          >
            <PersonalSignRawFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="personal_sign (SIWE)"
            title="Sign Message — SIWE (Authenticate)"
            top={10850}
          >
            <PersonalSignSiweFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="eth_signTypedData_v4"
            title="Sign Typed Data — Generic"
            top={11450}
          >
            <TypedDataFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="eth_signTypedData_v4 (invalid)"
            title="Sign Typed Data — Invalid"
            top={12050}
          >
            <TypedDataInvalidFlow />
          </FlowRow>

          <FlowRow
            left={60}
            method="eth_signTypedData_v4 (ERC-2612 Permit)"
            title="Sign Typed Data — Permit"
            top={12650}
          >
            <PermitFlow />
          </FlowRow>

          <FlowRow left={60} method="wallet_transfer" title="Send Token" top={13250}>
            <SendTokenFlow />
          </FlowRow>

          <FlowRow left={60} method="wallet_revokeAccessKey" title="Revoke Access Key" top={13950}>
            <RevokeAccessKeyFlow />
          </FlowRow>

          <FlowRow left={60} method="wallet_swap" title="Swap Token" top={14250}>
            <SwapTokenFlow />
          </FlowRow>

          <FlowRow left={60} method="wallet_depositZone" title="Deposit to Zone" top={14950}>
            <ZoneDepositFlow />
          </FlowRow>

          <FlowRow left={60} method="wallet_withdrawZone" title="Withdraw from Zone" top={15550}>
            <ZoneWithdrawFlow />
          </FlowRow>
        </div>
      </div>
    </div>
  )
}

/** A titled row on the canvas containing a flow's screens. */
function FlowRow(props: {
  children: ReactNode
  left: number
  method?: string | undefined
  title: string
  top: number
}) {
  const { children, left, method, title, top } = props
  return (
    <div className="absolute flex flex-col gap-6" style={{ left, top }}>
      <div className="flex flex-col gap-1">
        <h2 className="text-heading-20 text-foreground-secondary">{title}</h2>
        {method && <p className="font-mono text-label-12 text-foreground-secondary/60">{method}</p>}
      </div>
      <div className="flex items-start gap-8">{children}</div>
    </div>
  )
}

/** Arrow connector between screen cards. */
function Arrow() {
  return (
    <div className="flex h-full items-center self-center pt-6">
      <ArrowRight className="size-5 text-foreground-secondary/40" />
    </div>
  )
}

/** 360px dialog frame wrapper for wireframe cards. */
function Card(props: { children: ReactNode; label?: string | undefined }) {
  const { children, label } = props
  return (
    <div className="flex flex-col gap-2">
      {label && <p className="text-label-12 text-foreground-secondary">{label}</p>}
      <div className="panzoom-exclude w-[360px] rounded-body border border-border bg-surface">
        {children}
      </div>
    </div>
  )
}

/** New user + authorize: Sign In → Authorize. */
function ConnectNewUserAuthorizeLazyFlow() {
  return (
    <>
      <Card label="Sign In">
        <ConnectFrames.SignIn host="example.com" />
      </Card>

      <Arrow />

      <Card label="Authorize App">
        <AuthorizeFrames.AuthorizeSpend authorizeAccessKey={mockAuthorize} host="example.com" />
      </Card>
    </>
  )
}

/** New user flow: Sign In (create account triggers passkey creation). */
function ConnectNewUserFlow() {
  return (
    <Card label="Sign In">
      <ConnectFrames.SignIn host="example.com" />
    </Card>
  )
}

/** New user + deposit: Sign In → optional funding prompt → deposit overlay. */
function ConnectNewUserDepositFlow() {
  return (
    <>
      <Card label="Sign In">
        <ConnectFrames.SignIn host="example.com" />
      </Card>

      <Arrow />

      <Card label="Fund Account">
        <PostConnectDepositFrame />
      </Card>

      <Arrow />

      <Card label="Deposit Crypto">
        <TransactionFrames.DepositCrypto
          address={mockAddress}
          depositAddress="0x9f8e7d6c5b4a3928000000001e0f3c21d7a4b600"
          onBack={() => {}}
          onDone={() => {}}
        />
      </Card>
    </>
  )
}

/** New user + authorize + deposit: Sign In → Authorize → optional funding prompt. */
function ConnectNewUserAuthorizeDepositFlow() {
  return (
    <>
      <Card label="Sign In">
        <ConnectFrames.SignIn host="example.com" />
      </Card>

      <Arrow />

      <Card label="Authorize App">
        <AuthorizeFrames.AuthorizeSpend authorizeAccessKey={mockAuthorize} host="example.com" />
      </Card>

      <Arrow />

      <Card label="Fund Account">
        <PostConnectDepositFrame />
      </Card>
    </>
  )
}

/** New user, email label: Sign In (email) → verify code (entry skipped). */
function ConnectNewUserIdentityEmailFlow() {
  return (
    <>
      <Card label="Sign In (email)">
        <ConnectFrames.SignIn host="example.com" />
      </Card>

      <Arrow />

      <Card label="Check Email">
        <ConnectFrames.VerifyOtp
          code=""
          email="john@example.com"
          onBack={() => {}}
          onChange={() => {}}
          onResend={() => {}}
          resendStatus="idle"
        />
      </Card>
    </>
  )
}

/** New user, non-email label: Sign In → enter email → verify code (OTP). */
function ConnectNewUserIdentityLabelFlow() {
  return (
    <>
      <Card label="Sign In (label)">
        <ConnectFrames.SignIn host="example.com" />
      </Card>

      <Arrow />

      <Card label="Share Email (non-address)">
        <ConnectFrames.RequireEmail host="example.com" onSkip={() => {}} onSubmit={() => {}} />
      </Card>

      <Arrow />

      <Card label="Check Email">
        <ConnectFrames.VerifyOtp
          code=""
          email="john@example.com"
          onBack={() => {}}
          onChange={() => {}}
          onResend={() => {}}
          resendStatus="idle"
        />
      </Card>
    </>
  )
}

/** Returning user, email label: Welcome Back → verify code (OTP, auto-sent). */
function ConnectReturningIdentityVerifyFlow() {
  return (
    <>
      <Card label="Welcome Back">
        <ConnectFrames.WelcomeBack
          address="0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9e8f"
          host="example.com"
          label="john@example.com"
          onSignIn={() => {}}
        />
      </Card>

      <Arrow />

      <Card label="Check Email">
        <ConnectFrames.VerifyOtp
          code=""
          email="john@example.com"
          onBack={() => {}}
          onChange={() => {}}
          onResend={() => {}}
          resendStatus="idle"
        />
      </Card>
    </>
  )
}

/** Verified email with multiple accounts: choose one before WebAuthn. */
function ConnectSelectAccountFlow() {
  return (
    <Card label="Choose Account">
      <ConnectFrames.SelectAccount
        accounts={[
          {
            address: '0xc4b7cadcb881ce3025163a71ec271b75e6cdc120',
            credentialId: 'credential-1',
            recent: false,
            username: 'owner@example.com',
          },
          {
            address: '0xbaa114f539bdc9c7775ba7f0199f0787147e8171',
            credentialId: 'credential-2',
            passkeyProvider: 'Apple Passwords',
            recent: true,
            username: 'savings',
          },
        ]}
        email="owner@example.com"
        onBack={() => {}}
        onSelect={() => {}}
      />
    </Card>
  )
}

/** Returning user with existing passkey — single screen. */
function ConnectReturningPasskeyFlow() {
  return (
    <Card label="Welcome Back">
      <ConnectFrames.WelcomeBack
        address="0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9e8f"
        host="example.com"
        label="john@example.com"
        onSignIn={() => {}}
      />
    </Card>
  )
}

/** Returning user via passkey — Sign In screen with "Sign in with passkey". */
function ConnectReturningEmailFlow() {
  return (
    <Card label="Sign In">
      <ConnectFrames.SignIn host="example.com" />
    </Card>
  )
}

/** Returning user + authorize: Sign In → Authorize. */
function ConnectReturningEmailAuthorizeFlow() {
  return (
    <>
      <Card label="Sign In">
        <ConnectFrames.SignIn host="example.com" />
      </Card>

      <Arrow />

      <Card label="Authorize">
        <AuthorizeFrames.AuthorizeSpend authorizeAccessKey={mockAuthorize} host="example.com" />
      </Card>
    </>
  )
}

/** Returning user with existing passkey + authorize — scopes shown inline. */
function ConnectReturningPasskeyAuthorizeFlow() {
  return (
    <Card label="Welcome Back + Authorize">
      <ConnectFrames.WelcomeBack
        address="0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9e8f"
        authorizeAccessKey={mockAuthorize}
        host="example.com"
        label="john@example.com"
        onSignIn={() => {}}
      />
    </Card>
  )
}

const mockAuthorize = {
  expiry: Math.floor(Date.now() / 1000) + 86400,
  limits: [
    {
      token: '0x20c0000000000000000000000000000000000000' as `0x${string}`,
      limit: 100_000_000n,
      period: 3600,
    },
  ],
}

const mockAddress = '0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9e8f' as const

function OrDivider() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-0 flex-1 border-t border-border" />
      <p className="text-label-12 text-foreground-secondary">or</p>
      <div className="h-0 flex-1 border-t border-border" />
    </div>
  )
}

function PostConnectDepositFrame(props: {
  amount?: string | undefined
  continueAction?: boolean | undefined
  event?: ShowDeposit.Event | undefined
  host?: string | undefined
  subtitle?: string | undefined
  token?: string | undefined
}) {
  const {
    amount = '50',
    continueAction = true,
    event = 'register',
    host = 'example.com',
    subtitle,
    token,
  } = props
  const deposit = useDeposit({
    address: mockAddress,
    amount,
    chainId: tempo.id,
    onSuccess: () => {},
    orActions: continueAction ? (
      <Button className="w-full" size="medium" type="button" variant="secondary">
        Skip and continue
      </Button>
    ) : null,
    successScreen: false,
    token,
  })

  if (deposit.frame) return <Frame.Provider>{deposit.frame}</Frame.Provider>

  return (
    <Frame
      bodyClassName="gap-2"
      subtitle={subtitle ?? ShowDeposit.getSubtitle({ amount, event, host, token })}
      title="Fund your account"
    >
      {deposit.actions}
    </Frame>
  )
}

const mockBalanceDiffs: TransactionFrames.Generic.Props['balanceDiffs'] = [
  {
    address: '0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b' as `0x${string}`,
    decimals: 6,
    direction: 'outgoing',
    formatted: '50.00',
    name: 'USD Coin',
    recipients: ['0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b' as `0x${string}`],
    symbol: 'USDC',
    value: '0x2faf080' as `0x${string}`,
  },
  {
    address: '0xab12cd34ef56789012345678901234567890abcd' as `0x${string}`,
    decimals: 6,
    direction: 'incoming',
    formatted: '49.85',
    name: 'Bridged USDC',
    recipients: ['0xab12cd34ef56789012345678901234567890abcd' as `0x${string}`],
    symbol: 'USDC.e',
    value: '0x2f8a4a0' as `0x${string}`,
  },
]

const mockFee: TransactionFrames.Generic.Props['fee'] = {
  amount: '0x6d46' as `0x${string}`,
  decimals: 6,
  formatted: '0.028022',
  symbol: 'pathUSD',
}

const mockVirtualAddress = '0x58e21090fdfdfdfdfdfdfdfdfdfd010203040506' as const

const mockVirtualMasterAddress = '0xab12cd34ef56789012345678901234567890abcd' as const

const mockVirtualAddresses = {
  [mockVirtualAddress]: mockVirtualMasterAddress,
} satisfies TransactionFrames.Generic.Props['virtualAddresses']

/** Mock insufficient-balance alert data — mirrors the value returned by `useRequireFunds`. */
function mockAlert(amount: string) {
  return { type: 'warning', message: `You need to deposit ${amount} to continue.` } as const
}

/** Standard transaction: loading → review → confirming */
function SendTransactionFlow() {
  return (
    <>
      <Card label="Loading">
        <TransactionFrames.Generic loading />
      </Card>

      <Arrow />

      <Card label="Review">
        <TransactionFrames.Generic balanceDiffs={mockBalanceDiffs} fee={mockFee} />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <TransactionFrames.Generic balanceDiffs={mockBalanceDiffs} confirming fee={mockFee} />
      </Card>
    </>
  )
}

/** Insufficient balance → Add Funds */
function SendTransactionOnrampFlow() {
  return (
    <>
      <Card label="Insufficient Balance">
        <TransactionFrames.Generic
          alert={mockAlert('$50.00')}
          balanceDiffs={mockBalanceDiffs}
          fee={mockFee}
        />
      </Card>

      <Arrow />

      <Card label="Deposit Crypto">
        <TransactionFrames.DepositCrypto
          address="0x9f8e7d6c5b4a3928000000001e0f3c21d7a4b600"
          onBack={() => {}}
          onDone={() => {}}
        />
      </Card>
    </>
  )
}

/** Sponsored + auto-swap variants */
function SendTransactionExtrasFlow() {
  return (
    <>
      <Card label="Sponsored">
        <TransactionFrames.Generic
          balanceDiffs={mockBalanceDiffs}
          fee={mockFee}
          sponsor={{
            address: '0x0000000000000000000000000000000000000000' as `0x${string}`,
            name: 'My App',
            url: 'https://myapp.com',
          }}
        />
      </Card>

      <Arrow />

      <Card label="Auto Swap">
        <TransactionFrames.Generic
          autoSwap={{
            calls: [],
            maxIn: {
              decimals: 6,
              formatted: '105.00',
              name: 'AlphaUSD',
              symbol: 'AlphaUSD',
              token: '0x0000000000000000000000000000000000000001' as `0x${string}`,
              value: '0x6422c40' as `0x${string}`,
            },
            minOut: {
              decimals: 6,
              formatted: '100.00',
              name: 'Bridged USDC',
              symbol: 'USDC.e',
              token: '0x0000000000000000000000000000000000000002' as `0x${string}`,
              value: '0x5f5e100' as `0x${string}`,
            },
            slippage: 0.05,
          }}
          balanceDiffs={[
            {
              address: '0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b' as `0x${string}`,
              decimals: 6,
              direction: 'outgoing',
              formatted: '100.00',
              name: 'Bridged USDC',
              recipients: ['0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b' as `0x${string}`],
              symbol: 'USDC.e',
              value: '0x5f5e100' as `0x${string}`,
            },
          ]}
          fee={mockFee}
        />
      </Card>

      <Arrow />

      <Card label="Error">
        <TransactionFrames.Generic
          alert={{ type: 'error', message: 'Transaction reverted: execution exceeded gas limit.' }}
          balanceDiffs={mockBalanceDiffs}
          fee={mockFee}
        />
      </Card>

      <Arrow />

      <Card label="Virtual Address">
        <TransactionFrames.Generic
          balanceDiffs={[
            {
              address: '0x20c0000000000000000000000000000000000000' as `0x${string}`,
              decimals: 6,
              direction: 'outgoing',
              formatted: '25.00',
              name: 'PathUSD',
              recipients: [mockVirtualAddress],
              symbol: 'PathUSD',
              value: '0x17d7840' as `0x${string}`,
            },
          ]}
          fee={mockFee}
          virtualAddresses={mockVirtualAddresses}
        />
      </Card>

      <Arrow />

      <Card label="Non-TIP-20 Virtual Address">
        <TransactionFrames.Generic
          alert={{
            type: 'error',
            message: 'Non-TIP-20 tokens sent to virtual addresses are unrecoverable.',
          }}
          balanceDiffs={[
            {
              address: '0x1111111111111111111111111111111111111111' as `0x${string}`,
              decimals: 6,
              direction: 'outgoing',
              formatted: '25.00',
              name: 'USD Coin',
              recipients: [mockVirtualAddress],
              symbol: 'USDC',
              value: '0x17d7840' as `0x${string}`,
            },
          ]}
          fee={mockFee}
          virtualAddresses={mockVirtualAddresses}
        />
      </Card>
    </>
  )
}

const mockPaymentFee: TransactionFrames.Payment.Props['fee'] = {
  amount: '0x2654' as `0x${string}`,
  decimals: 6,
  formatted: '0.009812',
  symbol: 'pathUSD',
}

const mockPaymentDiff = {
  address: '0xab12cd34ef56789012345678901234567890abcd' as `0x${string}`,
  decimals: 6,
  direction: 'outgoing',
  formatted: '50.00',
  name: 'Bridged USDC',
  recipients: ['0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b' as `0x${string}`],
  symbol: 'USDC.e',
  value: '0x2faf080' as `0x${string}`,
} satisfies NonNullable<TransactionFrames.Payment.Props['balanceDiffs']>[number]

const mockPaymentDiffs: TransactionFrames.Payment.Props['balanceDiffs'] = [mockPaymentDiff]

/** Direct payment: loading → review → confirming */
function DirectPaymentFlow() {
  return (
    <>
      <Card label="Loading">
        <TransactionFrames.Payment loading />
      </Card>

      <Arrow />

      <Card label="Review">
        <TransactionFrames.Payment
          balanceDiffs={mockPaymentDiffs}
          fee={mockPaymentFee}
          host="example.com"
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <TransactionFrames.Payment
          balanceDiffs={mockPaymentDiffs}
          confirming
          fee={mockPaymentFee}
          host="example.com"
        />
      </Card>
    </>
  )
}

const mockApprovalFee: TransactionFrames.Approval.Props['fee'] = {
  amount: '0x2710' as `0x${string}`,
  decimals: 6,
  formatted: '0.010000',
  symbol: 'pathUSD',
}

const mockVaultDepositDiffs: TransactionFrames.Vault.Props['balanceDiffs'] = [
  {
    address: '0x20c0000000000000000000000000000000000000' as `0x${string}`,
    decimals: 6,
    direction: 'outgoing',
    formatted: '0.93',
    name: 'PathUSD',
    recipients: ['0x9a044ae05e5e6290dcf56afd69548565e957a626' as `0x${string}`],
    symbol: 'pathUSD',
    value: '0xe30c0' as `0x${string}`,
  },
]

const mockVaultWithdrawDiffs: TransactionFrames.Vault.Props['balanceDiffs'] = [
  {
    address: '0x20c0000000000000000000000000000000000000' as `0x${string}`,
    decimals: 6,
    direction: 'incoming',
    formatted: '0.93',
    name: 'PathUSD',
    recipients: ['0x76a21038ea44d8942c0765175f05d34fcfb838ab' as `0x${string}`],
    symbol: 'pathUSD',
    value: '0xe30c0' as `0x${string}`,
  },
]

const mockVaultAddress = '0x9a044ae05e5e6290dcf56afd69548565e957a626' as `0x${string}`

/** Token approval transaction: loading → review → confirming → sponsored */
function TokenApprovalFlow() {
  return (
    <>
      <Card label="Loading">
        <TransactionFrames.Approval
          amount={0n}
          loading
          spender="0x3a2a86bd16629b054f7f3d87f0a7570f3881a43c"
          tokenContract="0x20c0000000000000000000000000000000000000"
        />
      </Card>

      <Arrow />

      <Card label="Review">
        <TransactionFrames.Approval
          amount={2_000_000n}
          fee={mockApprovalFee}
          host="example.com"
          spender="0x3a2a86bd16629b054f7f3d87f0a7570f3881a43c"
          tokenContract="0x20c0000000000000000000000000000000000000"
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <TransactionFrames.Approval
          amount={2_000_000n}
          confirming
          fee={mockApprovalFee}
          host="example.com"
          spender="0x3a2a86bd16629b054f7f3d87f0a7570f3881a43c"
          tokenContract="0x20c0000000000000000000000000000000000000"
        />
      </Card>

      <Arrow />

      <Card label="Sponsored">
        <TransactionFrames.Approval
          amount={2_000_000n}
          fee={mockApprovalFee}
          host="example.com"
          spender="0x3a2a86bd16629b054f7f3d87f0a7570f3881a43c"
          sponsor={{
            address: '0x0000000000000000000000000000000000000000' as `0x${string}`,
            name: 'My App',
          }}
          tokenContract="0x20c0000000000000000000000000000000000000"
        />
      </Card>
    </>
  )
}

/** Vault transaction: loading → deposit review → withdraw review */
function VaultTransactionFlow() {
  return (
    <>
      <Card label="Loading">
        <TransactionFrames.Vault kind="deposit" loading vault={mockVaultAddress} />
      </Card>

      <Arrow />

      <Card label="Deposit">
        <TransactionFrames.Vault
          balanceDiffs={mockVaultDepositDiffs}
          fee={mockFee}
          kind="deposit"
          vault={mockVaultAddress}
        />
      </Card>

      <Arrow />

      <Card label="Withdraw">
        <TransactionFrames.Vault
          balanceDiffs={mockVaultWithdrawDiffs}
          fee={mockFee}
          kind="withdraw"
          vault={mockVaultAddress}
        />
      </Card>
    </>
  )
}

/** Terminal request result states. */
function RequestResultFlow() {
  return (
    <>
      <Card label="Approved">
        <RequestFrames.Result
          description={
            <span className="text-label-13">
              You can return to <span className="text-foreground">Accounts playground</span>
            </span>
          }
          title="Request approved"
          variant="success"
        />
      </Card>

      <Arrow />

      <Card label="Rejected">
        <RequestFrames.Result
          description={
            <>
              No wallet action was completed.
              <br />
              <span className="text-label-13">
                Return to <span className="text-foreground">Accounts playground</span> for details.
              </span>
            </>
          }
          title="Request rejected"
          variant="error"
        />
      </Card>

      <Arrow />

      <Card label="Failed">
        <RequestFrames.Result
          description={
            <>
              The request failed.
              <br />
              <span className="text-label-13">
                Return to <span className="text-foreground">Accounts playground</span> for details.
              </span>
            </>
          }
          title="Request failed"
          variant="error"
        />
      </Card>

      <Arrow />

      <Card label="Expired">
        <RequestFrames.Result
          description={
            <>
              The request expired.
              <br />
              <span className="text-label-13">
                Return to <span className="text-foreground">Accounts playground</span> for details.
              </span>
            </>
          }
          title="Request expired"
          variant="error"
        />
      </Card>
    </>
  )
}

/** Mobile authorization: review → confirming */
function AuthorizeMobileFlow() {
  return (
    <>
      <Card label="Review">
        <AuthorizeFrames.AuthorizeSpend authorizeAccessKey={mockAuthorize} host="DoorDash" />
      </Card>

      <Arrow />

      <Card label="Edit Non-expiring Permissions">
        <AccessKeyPermissionsEditor
          authorizeAccessKey={{ ...mockAuthorize, expiry: 0 }}
          expiry_original={0}
          onCancel={() => {}}
          onSave={() => {}}
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <AuthorizeFrames.AuthorizeSpend
          authorizeAccessKey={mockAuthorize}
          confirming
          host="DoorDash"
        />
      </Card>
    </>
  )
}

/** Mobile registration: create account → optional funding prompt. */
function AuthorizeMobileRegisterDepositFlow() {
  return (
    <>
      <Card label="Create Account">
        <AuthRegisterFrame
          subtitle="Authorize this app to spend from your account."
          title="Authorize Mobile"
        />
      </Card>

      <Arrow />

      <Card label="Fund Account">
        <PostConnectDepositFrame />
      </Card>
    </>
  )
}

function AuthRegisterFrame(props: { subtitle: string; title: string }) {
  const { subtitle, title } = props
  return (
    <Frame
      footerClassName="pt-0"
      footer={
        <form className="flex flex-col gap-4">
          <p className="text-copy-15 text-foreground-secondary">
            Create or sign in with your passkey to continue.
          </p>
          <Input name="label" placeholder="Email address or label…" required />
          <Button type="submit" variant="primary">
            Create account
          </Button>
          <OrDivider />
          <Button icon={<Fingerprint className="size-4" />} type="button" variant="secondary">
            Sign in with passkey
          </Button>
        </form>
      }
      subtitle={subtitle}
      title={title}
    >
      <AccessKeyScopes authorizeAccessKey={mockAuthorize} />
    </Frame>
  )
}

/** Deposit: amount input → crypto deposit */
function DepositFlow() {
  return (
    <>
      <Card label="Enter Amount">
        <TransactionFrames.Deposit />
      </Card>

      <Arrow />

      <Card label="Crypto Deposit">
        <TransactionFrames.DepositCrypto
          address="0x9f8e7d6c5b4a3928000000001e0f3c21d7a4b600"
          depositAddress="0x9f8e7d6c5b4a3928000000001e0f3c21d7a4b600"
          onBack={() => {}}
          onDone={() => {}}
        />
      </Card>
    </>
  )
}

/** Personal sign: loading → review → confirming */
function PersonalSignFlow() {
  return (
    <>
      <Card label="Review">
        <SignFrames.PersonalSign
          host="example.com"
          message="Hello! Please sign this message to verify your identity. Nonce: 8a3b9c7d"
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <SignFrames.PersonalSign
          confirming
          host="example.com"
          message="Hello! Please sign this message to verify your identity. Nonce: 8a3b9c7d"
        />
      </Card>
    </>
  )
}

/** Personal sign with raw hex data that cannot be decoded to UTF-8 */
function PersonalSignRawFlow() {
  return (
    <>
      <Card label="Review (Raw)">
        <SignFrames.PersonalSign
          host="example.com"
          message="0x9c22ff5f21f0b81b113e63f7db6da94fedef11b2119b4088b89664fb9a3cb658"
          raw
        />
      </Card>

      <Arrow />

      <Card label="Confirming (Raw)">
        <SignFrames.PersonalSign
          confirming
          host="example.com"
          message="0x9c22ff5f21f0b81b113e63f7db6da94fedef11b2119b4088b89664fb9a3cb658"
          raw
        />
      </Card>
    </>
  )
}

/** SIWE authentication: simplified approve screen — no message body shown */
function PersonalSignSiweFlow() {
  return (
    <>
      <Card label="Authenticate">
        <SignFrames.Siwe host="example.com" />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <SignFrames.Siwe confirming host="example.com" />
      </Card>
    </>
  )
}

const mockTypedData: SignFrames.TypedData.Data = {
  domain: { name: 'Example App' },
  message: {
    from: { name: 'Alice', wallet: '0x0000000000000000000000000000000000000001' },
    to: { name: 'Bob', wallet: '0x0000000000000000000000000000000000000002' },
    contents: 'Hello, Bob!',
  },
  primaryType: 'Mail',
  types: {
    EIP712Domain: [
      { name: 'name', type: 'string' },
      { name: 'version', type: 'string' },
      { name: 'chainId', type: 'uint256' },
    ],
    Person: [
      { name: 'name', type: 'string' },
      { name: 'wallet', type: 'address' },
    ],
    Mail: [
      { name: 'from', type: 'Person' },
      { name: 'to', type: 'Person' },
      { name: 'contents', type: 'string' },
    ],
  },
}

/** Generic typed data: review → confirming */
function TypedDataFlow() {
  return (
    <>
      <Card label="Review">
        <SignFrames.TypedData data={mockTypedData} host="example.com" />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <SignFrames.TypedData confirming data={mockTypedData} host="example.com" />
      </Card>
    </>
  )
}

/** Invalid typed data: warning + destructive sign anyway */
function TypedDataInvalidFlow() {
  return (
    <>
      <Card label="Review (Invalid)">
        <SignFrames.TypedDataInvalid
          data='{"primaryType":"Transfer","message":{"to":"0x1234...","amount":"100"}}'
          host="example.com"
        />
      </Card>

      <Arrow />

      <Card label="Confirming (Invalid)">
        <SignFrames.TypedDataInvalid
          confirming
          data='{"primaryType":"Transfer","message":{"to":"0x1234...","amount":"100"}}'
          host="example.com"
        />
      </Card>
    </>
  )
}

/** ERC-2612 Permit: approve → confirming */
function PermitFlow() {
  return (
    <>
      <Card label="Approve">
        <SignFrames.Permit
          amount={100_000_000n}
          chainId={98865}
          deadline={Math.floor(Date.now() / 1000) + 86400}
          host="example.com"
          permitType="erc-2612"
          spender={'0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b' as `0x${string}`}
          tokenContract={'0x20c0000000000000000000000000000000000000' as `0x${string}`}
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <SignFrames.Permit
          amount={100_000_000n}
          chainId={98865}
          confirming
          deadline={Math.floor(Date.now() / 1000) + 86400}
          host="example.com"
          permitType="erc-2612"
          spender={'0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b' as `0x${string}`}
          tokenContract={'0x20c0000000000000000000000000000000000000' as `0x${string}`}
        />
      </Card>
    </>
  )
}

/** Deposit to a specific recipient (e.g. DoorDash) */
function DepositRecipientFlow() {
  return (
    <>
      <Card label="Enter Amount">
        <TransactionFrames.Deposit
          subtitle={
            <>
              Deposit funds to <span className="text-foreground">DoorDash</span>.
            </>
          }
        />
      </Card>

      <Arrow />

      <Card label="Crypto Deposit">
        <TransactionFrames.DepositCrypto
          address="0x9f8e7d6c5b4a3928000000001e0f3c21d7a4b600"
          depositAddress="0x9f8e7d6c5b4a3928000000001e0f3c21d7a4b600"
          onBack={() => {}}
          onDone={() => {}}
        />
      </Card>
    </>
  )
}

/** Sponsored + auto-swap variants */
function DirectPaymentExtrasFlow() {
  return (
    <>
      <Card label="Sponsored">
        <TransactionFrames.Payment
          balanceDiffs={mockPaymentDiffs}
          fee={mockPaymentFee}
          host="example.com"
          sponsor={{
            address: '0x0000000000000000000000000000000000000000' as `0x${string}`,
            name: 'My App',
          }}
        />
      </Card>

      <Arrow />

      <Card label="Auto Swap">
        <TransactionFrames.Payment
          autoSwap={{
            calls: [],
            maxIn: {
              formatted: '52.50',
              symbol: 'AlphaUSD',
              decimals: 6,
              name: 'AlphaUSD',
              token: '0x0000000000000000000000000000000000000001' as `0x${string}`,
              value: '0x0' as `0x${string}`,
            },
            minOut: {
              formatted: '50.00',
              symbol: 'USDC.e',
              decimals: 6,
              name: 'Bridged USDC',
              token: '0x0000000000000000000000000000000000000002' as `0x${string}`,
              value: '0x0' as `0x${string}`,
            },
            slippage: 0.05,
          }}
          balanceDiffs={mockPaymentDiffs}
          fee={mockPaymentFee}
          host="example.com"
        />
      </Card>

      <Arrow />

      <Card label="Virtual Address">
        <TransactionFrames.Payment
          balanceDiffs={[
            {
              ...mockPaymentDiff,
              address: '0x20c0000000000000000000000000000000000000' as `0x${string}`,
              name: 'PathUSD',
              recipients: [mockVirtualAddress],
              symbol: 'PathUSD',
            },
          ]}
          fee={mockPaymentFee}
          host="example.com"
          virtualAddresses={mockVirtualAddresses}
        />
      </Card>

      <Arrow />

      <Card label="Non-TIP-20 Virtual Address">
        <TransactionFrames.Payment
          alert={{
            type: 'error',
            message: 'Non-TIP-20 tokens sent to virtual addresses are unrecoverable.',
          }}
          balanceDiffs={[
            {
              ...mockPaymentDiff,
              address: '0x1111111111111111111111111111111111111111' as `0x${string}`,
              name: 'USD Coin',
              recipients: [mockVirtualAddress],
              symbol: 'USDC',
            },
          ]}
          fee={mockPaymentFee}
          host="example.com"
          virtualAddresses={mockVirtualAddresses}
        />
      </Card>
    </>
  )
}

const mockSendPrefill = {
  to: '0xAb5801a7D398351b8bE11C439e05C5B3259aeC9B',
  token: '0x20c0000000000000000000000000000000000000',
  value: '10',
}

const mockSendVirtualPrefill = {
  ...mockSendPrefill,
  to: mockVirtualAddress,
}

const mockSendAsset = {
  address: mockSendPrefill.token,
  balance: '999,760',
  symbol: 'PathUSD',
}

const mockSendChainId = 7702

const mockSwapAssetIn = {
  address: '0x20c0000000000000000000000000000000000000',
  balance: '999,760',
  symbol: 'PathUSD',
}

const mockSwapAssetOut = {
  address: '0x20c0000000000000000000000000000000000001',
  balance: '25,000',
  symbol: 'AlphaUSD',
}

const mockSwapAssetOther = {
  address: '0x20c0000000000000000000000000000000000002',
  balance: '10,000',
  symbol: 'BetaUSD',
}

const mockSwapUnsupportedIn = {
  address: '0x1111111111111111111111111111111111111111',
  balance: '10,000',
  symbol: 'USDC',
}

const mockSwapUnsupportedOut = {
  address: '0x2222222222222222222222222222222222222222',
  balance: '0',
  symbol: 'DAI',
}

const mockSwapAssets = [mockSwapAssetIn, mockSwapAssetOut, mockSwapAssetOther]

const mockSwapQuote = {
  decimals: 6,
  formatted: '99.500000',
  symbol: 'AlphaUSD',
  value: '0x5ee6820' as `0x${string}`,
}

const mockSwapExactOutQuote = {
  decimals: 6,
  formatted: '100.500000',
  symbol: 'PathUSD',
  value: '0x5fd8220' as `0x${string}`,
}

const mockSendErrorAction = {
  href: 'https://explore.moderato.tempo.xyz/receipt/0x0000000000000000000000000000000000000000000000000000000000000000',
  label: 'View',
}

/** wallet_transfer: empty → invalid params → fee pending → ready → confirming → error states */
function SendTokenFlow() {
  return (
    <>
      <Card label="Empty">
        <TransactionFrames.Send chainId={mockSendChainId} host="myapp.xyz" />
      </Card>

      <Arrow />

      <Card label="Invalid Token">
        <TransactionFrames.Send
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          errors={{ token: 'Invalid asset.' }}
          host="myapp.xyz"
          values={{ ...mockSendPrefill, token: 'not-an-address' }}
        />
      </Card>

      <Arrow />

      <Card label="Fee Pending">
        <TransactionFrames.Send
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          feePending
          host="myapp.xyz"
          selectedAsset={mockSendAsset}
          values={mockSendPrefill}
        />
      </Card>

      <Arrow />

      <Card label="Ready">
        <TransactionFrames.Send
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          fee={mockPaymentFee}
          host="myapp.xyz"
          ready
          selectedAsset={mockSendAsset}
          values={mockSendPrefill}
        />
      </Card>

      <Arrow />

      <Card label="Virtual Address">
        <TransactionFrames.Send
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          fee={mockPaymentFee}
          forwardedAddress={mockVirtualMasterAddress}
          host="myapp.xyz"
          memoSupported
          ready
          selectedAsset={mockSendAsset}
          values={mockSendVirtualPrefill}
        />
      </Card>

      <Arrow />

      <Card label="Non-TIP-20 Virtual Address">
        <TransactionFrames.Send
          alert={{
            type: 'error',
            message: 'Non-TIP-20 tokens sent to virtual addresses are unrecoverable.',
          }}
          assets={[mockSwapUnsupportedIn]}
          chainId={mockSendChainId}
          fee={mockPaymentFee}
          host="myapp.xyz"
          selectedAsset={mockSwapUnsupportedIn}
          values={{
            ...mockSendVirtualPrefill,
            token: mockSwapUnsupportedIn.address,
          }}
        />
      </Card>

      <Arrow />

      <Card label="Unregistered Virtual Address">
        <TransactionFrames.Send
          alert={{
            type: 'error',
            message: 'Virtual address is not registered.',
          }}
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          fee={mockPaymentFee}
          host="myapp.xyz"
          memoSupported
          selectedAsset={mockSendAsset}
          values={mockSendVirtualPrefill}
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <TransactionFrames.Send
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          confirming
          fee={mockPaymentFee}
          host="myapp.xyz"
          passkey={false}
          primaryLabel="Confirming…"
          ready
          selectedAsset={mockSendAsset}
          values={mockSendPrefill}
        />
      </Card>

      <Arrow />

      <Card label="Reverted / View">
        <TransactionFrames.Send
          alert={{ type: 'error', message: 'Transaction reverted.', action: mockSendErrorAction }}
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          fee={mockPaymentFee}
          host="myapp.xyz"
          primaryLabel="Try again"
          ready
          selectedAsset={mockSendAsset}
          values={mockSendPrefill}
        />
      </Card>

      <Arrow />

      <Card label="Insufficient Balance">
        <TransactionFrames.Send
          alert={{ type: 'error', message: 'Insufficient balance.', action: mockSendErrorAction }}
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          fee={mockPaymentFee}
          host="myapp.xyz"
          primaryLabel="Try again"
          ready
          selectedAsset={mockSendAsset}
          values={mockSendPrefill}
        />
      </Card>

      <Arrow />

      <Card label="Out of Gas">
        <TransactionFrames.Send
          alert={{
            type: 'error',
            message: 'Transaction ran out of gas.',
            action: mockSendErrorAction,
          }}
          assets={[mockSendAsset]}
          chainId={mockSendChainId}
          fee={mockPaymentFee}
          host="myapp.xyz"
          primaryLabel="Try again"
          ready
          selectedAsset={mockSendAsset}
          values={mockSendPrefill}
        />
      </Card>
    </>
  )
}

const mockRevokeAccount = '0x9f8e7d6c5b4a3928000000001e0f3c21d7a4b600' as const
const mockRevokeAccessKey = '0x4edc856d74cf86d3300c8f99a5cbe44c70102520' as const

/** wallet_revokeAccessKey: loading → review → confirming → error → insufficient balance */
function RevokeAccessKeyFlow() {
  return (
    <>
      <Card label="Loading">
        <TransactionFrames.RevokeAccessKey
          accessKeyAddress={mockRevokeAccessKey}
          accountAddress={mockRevokeAccount}
          host="example.com"
          loading
        />
      </Card>

      <Arrow />

      <Card label="Review">
        <TransactionFrames.RevokeAccessKey
          accessKeyAddress={mockRevokeAccessKey}
          accountAddress={mockRevokeAccount}
          fee={mockFee}
          host="example.com"
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <TransactionFrames.RevokeAccessKey
          accessKeyAddress={mockRevokeAccessKey}
          accountAddress={mockRevokeAccount}
          confirming
          fee={mockFee}
          host="example.com"
        />
      </Card>

      <Arrow />

      <Card label="Insufficient Balance">
        <TransactionFrames.RevokeAccessKey
          accessKeyAddress={mockRevokeAccessKey}
          accountAddress={mockRevokeAccount}
          alert={mockAlert('$0.05')}
          fee={mockFee}
          host="example.com"
        />
      </Card>

      <Arrow />

      <Card label="Error">
        <TransactionFrames.RevokeAccessKey
          accessKeyAddress={mockRevokeAccessKey}
          accountAddress={mockRevokeAccount}
          alert={{ type: 'error', message: 'Transaction reverted.' }}
          host="example.com"
        />
      </Card>
    </>
  )
}

/** wallet_swap: empty → quote pending → ready → confirming → error states */
function SwapTokenFlow() {
  return (
    <>
      <Card label="Empty">
        <TransactionFrames.Swap
          assets={mockSwapAssets}
          mode="exactIn"
          quote={{}}
          values={{ amount: '', chainId: mockSendChainId }}
        />
      </Card>

      <Arrow />

      <Card label="Unsupported Tokens">
        <TransactionFrames.Swap
          assets={[mockSwapUnsupportedIn, mockSwapUnsupportedOut, ...mockSwapAssets]}
          errors={{
            tokenIn: 'Only TIP-20 assets can be swapped.',
            tokenOut: 'Only TIP-20 assets can be swapped.',
          }}
          mode="exactIn"
          quote={{}}
          selectedAssetIn={mockSwapUnsupportedIn}
          selectedAssetOut={mockSwapUnsupportedOut}
          values={{ amount: '100', chainId: mockSendChainId }}
        />
      </Card>

      <Arrow />

      <Card label="Quote Pending">
        <TransactionFrames.Swap
          assets={mockSwapAssets}
          feePending
          mode="exactIn"
          quote={{ loading: true }}
          selectedAssetIn={mockSwapAssetIn}
          selectedAssetOut={mockSwapAssetOut}
          values={{ amount: '100', chainId: mockSendChainId }}
        />
      </Card>

      <Arrow />

      <Card label="Exact Input Ready">
        <TransactionFrames.Swap
          assets={mockSwapAssets}
          fee={mockPaymentFee}
          mode="exactIn"
          quote={{ amount: mockSwapQuote }}
          ready
          selectedAssetIn={mockSwapAssetIn}
          selectedAssetOut={mockSwapAssetOut}
          values={{ amount: '100', chainId: mockSendChainId }}
        />
      </Card>

      <Arrow />

      <Card label="Exact Output Ready">
        <TransactionFrames.Swap
          assets={mockSwapAssets}
          fee={mockPaymentFee}
          mode="exactOut"
          quote={{ amount: mockSwapExactOutQuote }}
          ready
          selectedAssetIn={mockSwapAssetIn}
          selectedAssetOut={mockSwapAssetOut}
          values={{ amount: '100', chainId: mockSendChainId }}
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <TransactionFrames.Swap
          assets={mockSwapAssets}
          confirming
          fee={mockPaymentFee}
          mode="exactIn"
          quote={{ amount: mockSwapQuote }}
          ready
          selectedAssetIn={mockSwapAssetIn}
          selectedAssetOut={mockSwapAssetOut}
          values={{ amount: '100', chainId: mockSendChainId }}
        />
      </Card>

      <Arrow />

      <Card label="Validation Error">
        <TransactionFrames.Swap
          assets={mockSwapAssets}
          errors={{ amount: 'Enter a larger amount.' }}
          mode="exactIn"
          quote={{ amount: mockSwapQuote }}
          selectedAssetIn={mockSwapAssetIn}
          selectedAssetOut={mockSwapAssetOut}
          values={{ amount: '0.0000001', chainId: mockSendChainId }}
        />
      </Card>

      <Arrow />

      <Card label="Quote Error">
        <TransactionFrames.Swap
          alert={{ type: 'error', message: 'No route found for this pair.' }}
          assets={mockSwapAssets}
          mode="exactIn"
          quote={{}}
          selectedAssetIn={mockSwapAssetIn}
          selectedAssetOut={mockSwapAssetOut}
          values={{ amount: '100', chainId: mockSendChainId }}
        />
      </Card>
    </>
  )
}

const mockZoneAsset = {
  address: '0x20c0000000000000000000000000000000000000',
  balance: '999,760',
  symbol: 'PathUSD',
}

const mockZonePrefill = { amount: '10', token: mockZoneAsset.address }

const mockZoneFee: ZoneFrames.Deposit.Props['fee'] = {
  amount: '0xc350' as `0x${string}`,
  decimals: 6,
  formatted: '0.050000',
  symbol: 'pathUSD',
}

/** wallet_depositZone: loading → empty → invalid → fee pending → ready → confirming → error states. */
function ZoneDepositFlow() {
  return (
    <>
      <Card label="Loading">
        <ZoneFrames.Deposit chainId={mockSendChainId} host="myapp.xyz" loading />
      </Card>

      <Arrow />

      <Card label="Empty">
        <ZoneFrames.Deposit chainId={mockSendChainId} host="myapp.xyz" />
      </Card>

      <Arrow />

      <Card label="Invalid Amount">
        <ZoneFrames.Deposit
          assets={[mockZoneAsset]}
          chainId={mockSendChainId}
          errors={{ amount: 'Insufficient balance.' }}
          host="myapp.xyz"
          selectedAsset={mockZoneAsset}
          values={{ amount: '99,999,999', token: mockZoneAsset.address }}
        />
      </Card>

      <Arrow />

      <Card label="Fee Pending">
        <ZoneFrames.Deposit
          assets={[mockZoneAsset]}
          chainId={mockSendChainId}
          feePending
          host="myapp.xyz"
          selectedAsset={mockZoneAsset}
          values={mockZonePrefill}
        />
      </Card>

      <Arrow />

      <Card label="Ready">
        <ZoneFrames.Deposit
          assets={[mockZoneAsset]}
          chainId={mockSendChainId}
          fee={mockZoneFee}
          host="myapp.xyz"
          ready
          selectedAsset={mockZoneAsset}
          values={mockZonePrefill}
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <ZoneFrames.Deposit
          assets={[mockZoneAsset]}
          chainId={mockSendChainId}
          confirming
          fee={mockZoneFee}
          host="myapp.xyz"
          primaryLabel="Depositing…"
          ready
          selectedAsset={mockZoneAsset}
          values={mockZonePrefill}
        />
      </Card>

      <Arrow />

      <Card label="Reverted">
        <ZoneFrames.Deposit
          alert={{ type: 'error', message: 'Transaction reverted.' }}
          assets={[mockZoneAsset]}
          chainId={mockSendChainId}
          fee={mockZoneFee}
          host="myapp.xyz"
          primaryLabel="Try again"
          ready
          selectedAsset={mockZoneAsset}
          values={mockZonePrefill}
        />
      </Card>
    </>
  )
}

/** wallet_withdrawZone: loading → empty → ready → confirming → error states. */
function ZoneWithdrawFlow() {
  return (
    <>
      <Card label="Loading">
        <ZoneFrames.Withdraw chainId={mockSendChainId} host="myapp.xyz" loading />
      </Card>

      <Arrow />

      <Card label="Empty">
        <ZoneFrames.Withdraw chainId={mockSendChainId} host="myapp.xyz" />
      </Card>

      <Arrow />

      <Card label="Ready">
        <ZoneFrames.Withdraw
          assets={[mockZoneAsset]}
          chainId={mockSendChainId}
          host="myapp.xyz"
          ready
          selectedAsset={mockZoneAsset}
          values={mockZonePrefill}
        />
      </Card>

      <Arrow />

      <Card label="Confirming">
        <ZoneFrames.Withdraw
          assets={[mockZoneAsset]}
          chainId={mockSendChainId}
          confirming
          host="myapp.xyz"
          primaryLabel="Withdrawing…"
          ready
          selectedAsset={mockZoneAsset}
          values={mockZonePrefill}
        />
      </Card>

      <Arrow />

      <Card label="Reverted">
        <ZoneFrames.Withdraw
          alert={{ type: 'error', message: 'Transaction reverted.' }}
          assets={[mockZoneAsset]}
          chainId={mockSendChainId}
          host="myapp.xyz"
          primaryLabel="Try again"
          ready
          selectedAsset={mockZoneAsset}
          values={mockZonePrefill}
        />
      </Card>
    </>
  )
}
